import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './redux';
import { selectAgent } from '../../store/actions/agentActions';
import { findAgentByNameOrId } from '../../utils/agentUtils';

/**
 * Reads the `?agent=<name-or-id>` query parameter and auto-selects the
 * matching agent once the agent list has loaded. The parameter is consumed
 * (removed from the URL) after a successful match so it doesn't interfere
 * with subsequent navigation.
 */
export const useAgentFromUrl = (): void => {
  const dispatch = useAppDispatch();
  const agents = useAppSelector((state) => state.agent.agents);
  const [searchParams, setSearchParams] = useSearchParams();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || agents.length === 0) return;

    const agentParam = searchParams.get('agent');
    if (!agentParam) return;

    const match = findAgentByNameOrId(agents, agentParam);

    if (match) {
      selectAgent(dispatch, match);
      handled.current = true;

      // Remove the query param so the URL stays clean after selection
      searchParams.delete('agent');
      setSearchParams(searchParams, { replace: true });
    }
  }, [agents, searchParams, setSearchParams, dispatch]);
};
